import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/auth';
import { useAuthStore } from '../store/auth';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export function RegisterPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const handleSubmit = async () => {
    if (password.length < 8) {
      alert(t('auth.register.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      const resp = await authApi.registerEmail({ email, password, nickname: nickname || undefined });
      setAuth(resp.user, resp.tokens.accessToken, resp.tokens.refreshToken);
      navigate('/calendar', { replace: true });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || t('auth.register.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-center p-16 bg-black/25 backdrop-blur-sm border-r border-[var(--grid-line)]">
        <h1 className="font-display text-5xl font-medium leading-tight mb-4">
          {t('auth.register.brandHeading1')}<br />{t('auth.register.brandHeading2')}
        </h1>
        <p className="text-daynote-muted max-w-sm leading-relaxed">
          {t('auth.register.brandSubtitle')}
        </p>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-[400px]">
          <h1 className="font-display text-4xl font-medium mb-1.5">{t('auth.register.title')}</h1>
          <p className="text-[13px] text-daynote-muted mb-9">
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" className="text-daynote-accent border-b border-daynote-accent pb-px">
              {t('auth.register.loginLink')}
            </Link>
          </p>

          <div className="space-y-4">
            <div>
              <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                {t('auth.register.nicknameLabel')}
              </label>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={t('auth.register.nicknamePlaceholder')}
                className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                {t('auth.register.emailLabel')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                {t('auth.register.passwordLabel')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.register.passwordPlaceholder')}
                className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
              />
            </div>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="w-full mt-7 py-4 bg-daynote-accent text-[#2a2520] rounded-lg font-semibold text-sm tracking-widest hover:brightness-110 transition-all disabled:opacity-60 border-none cursor-pointer"
          >
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>

          <p className="text-[10px] text-daynote-text/40 text-center mt-7 leading-relaxed">
            {t('auth.register.termsPrefix')}{' '}
            <Link to="/terms" className="text-daynote-muted underline">
              {t('auth.register.termsLink')}
            </Link>{' '}
            {t('auth.register.termsAnd')}{' '}
            <Link to="/privacy" className="text-daynote-muted underline">
              {t('auth.register.privacyLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
